import { useState } from 'react';
import {
  ArrowsClockwise,
  CheckCircle,
  GitBranch,
  LockKey,
  PlugsConnected,
  WarningCircle,
} from '@phosphor-icons/react';

import {
  useConnectConflicts,
  useConnectStatus,
  useResolveConnectConflict,
  type ConnectConflictResolutionAction,
  type ConnectSyncConflict,
} from '@/features/connect';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { cn } from '@/shared/utils/cn';

function authorityLabel(value: string | null): string {
  switch (value) {
    case 'business':
      return 'Business kaynak sistem';
    case 'petservis':
      return 'PetServis kaynak sistem';
    case 'shared':
      return 'Paylaşımlı otorite';
    default:
      return 'Standalone';
  }
}

export function ConnectPage(): JSX.Element {
  const status = useConnectStatus();
  const conflicts = useConnectConflicts();
  const resolver = useResolveConnectConflict();
  const [resolutionNotes, setResolutionNotes] = useState<Record<string, string>>({});
  const data = status.data;
  const isActive = Boolean(data?.connectEnabled);

  function updateResolutionNote(conflictId: string, value: string): void {
    setResolutionNotes((prev) => ({ ...prev, [conflictId]: value }));
  }

  function submitResolution(
    conflict: ConnectSyncConflict,
    action: ConnectConflictResolutionAction,
  ): void {
    const reason = resolutionNotes[conflict.id]?.trim()
      || `Connect console ${action} action for ${conflict.conflictKind}.`;
    resolver.mutate({ conflictId: conflict.id, payload: { action, reason } });
  }

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-biz-section px-biz-card-lg py-biz-section">
      <section className="rounded-biz-card-lg border border-biz-border bg-biz-surface-elevated p-biz-card-lg shadow-biz-sm">
        <div className="flex flex-col gap-biz-content-lg lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-biz-xs font-semibold uppercase tracking-[0.22em] text-biz-primary">
              Phase 4 Connect
            </p>
            <h1 className="mt-biz-content text-biz-3xl font-semibold biz-heading">
              Marketplace bağlantısı
            </h1>
            <p className="mt-biz-content max-w-3xl text-biz-base text-biz-muted-foreground">
              Paptoq marketplace verisi ile Business operasyon paneli arasındaki
              mod, partner binding ve senkron otoritesini gösterir. Bu ilk dilim
              salt okunurdur; yazma/sync motoru ayrı gate ile açılacaktır.
            </p>
          </div>
          <div
            className={cn(
              'inline-flex min-h-biz-touch-primary items-center gap-biz-content rounded-biz-card px-biz-card py-biz-content text-biz-sm font-semibold',
              isActive
                ? 'bg-emerald-500/10 text-emerald-700'
                : 'bg-biz-muted text-biz-muted-foreground',
            )}
          >
            {isActive ? <CheckCircle size={22} weight="fill" /> : <LockKey size={22} />}
            {isActive ? 'Connect aktif' : 'Standalone mod'}
          </div>
        </div>
      </section>

      {status.isLoading ? (
        <Card>
          <CardBody className="py-biz-section text-biz-sm text-biz-muted-foreground">
            Connect durumu yükleniyor...
          </CardBody>
        </Card>
      ) : null}

      {status.isError ? (
        <Card>
          <CardBody className="py-biz-section text-biz-sm text-red-600">
            Connect durumu okunamadı. Lütfen tenant seçimini ve oturum yetkisini kontrol edin.
          </CardBody>
        </Card>
      ) : null}

      {data ? (
        <div className="grid gap-biz-content-lg lg:grid-cols-3">
          <StatusCard
            icon={<PlugsConnected size={26} />}
            title="Kullanım modu"
            value={data.mode === 'connect' ? 'Connect' : 'Standalone'}
            description={data.statusReason === 'connect_module_missing'
              ? 'Partner binding var, ancak M-CONNECT-01 modülü açık değil.'
              : data.mode === 'connect'
                ? 'Marketplace partner bağlantısı etkin.'
                : 'Bu işletme yalnız kendi müşteri tabanı ile çalışıyor.'}
          />
          <StatusCard
            icon={<GitBranch size={26} />}
            title="Partner binding"
            value={data.connectPartnerId ?? 'Yok'}
            description="ADR-116 binding modelinde partner aktör bağlantısı."
          />
          <StatusCard
            icon={<ArrowsClockwise size={26} />}
            title="Senkron otoritesi"
            value={authorityLabel(data.syncAuthority)}
            description="Faz 4 write path açıldığında çakışma çözümünün kaynak kararı."
          />
        </div>
      ) : null}

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-biz-content md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="font-semibold biz-heading">Senkron uzlaştırma kuyruğu</h2>
              <p className="text-biz-sm text-biz-muted-foreground">
                Marketplace event worker tarafından yakalanan booking değişiklikleri ve conflict kayıtları.
              </p>
            </div>
            <div className="inline-flex min-h-biz-touch-primary items-center gap-biz-content rounded-biz-card bg-biz-muted px-biz-card text-biz-sm font-semibold text-biz-muted-foreground">
              <WarningCircle size={20} />
              {conflicts.data?.items.filter((item) => item.status !== 'resolved' && item.status !== 'ignored').length ?? 0}
              {' '}açık kayıt
            </div>
          </div>
        </CardHeader>
        <CardBody className="space-y-biz-content">
          {conflicts.isLoading ? (
            <p className="text-biz-sm text-biz-muted-foreground">Conflict kayıtları yükleniyor...</p>
          ) : null}
          {conflicts.isError ? (
            <p className="text-biz-sm text-red-600">Conflict kayıtları okunamadı.</p>
          ) : null}
          {conflicts.data?.items.length === 0 ? (
            <p className="text-biz-sm text-biz-muted-foreground">
              Açık reconciliation kaydı yok. Worker yeni marketplace event aldığında burada görünür.
            </p>
          ) : null}
          {conflicts.data?.items.map((conflict) => (
            <ConflictCard
              key={conflict.id}
              conflict={conflict}
              note={resolutionNotes[conflict.id] ?? ''}
              isResolving={resolver.isPending}
              onNoteChange={(value) => updateResolutionNote(conflict.id, value)}
              onResolve={(action) => submitResolution(conflict, action)}
            />
          ))}
        </CardBody>
      </Card>

      {data ? (
        <Card>
          <CardHeader>
            <h2 className="font-semibold biz-heading">Açılacak sync yüzeyleri</h2>
            <p className="text-biz-sm text-biz-muted-foreground">
              Bu ekran ilk aşamada yetki ve görünürlük kanıtıdır. Aşağıdaki yazma yüzeyleri
              ayrı audit ve event gate ile açılır.
            </p>
          </CardHeader>
          <CardBody>
            <div className="grid gap-biz-content md:grid-cols-3">
              {[
                'Marketplace booking → biz_bookings external_booking_id linki',
                'Offering/catalog parity mapping ve fiyat otoritesi',
                'domain business event/outbox topic sözleşmesi',
              ].map((item) => (
                <div
                  key={item}
                  className="rounded-biz-card border border-dashed border-biz-border bg-biz-muted/30 p-biz-card text-biz-sm"
                >
                  {item}
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
}

function conflictKindLabel(kind: string): string {
  switch (kind) {
    case 'business_local_update':
      return 'Business local update';
    case 'marketplace_remote_update':
      return 'Marketplace remote update';
    case 'authority_violation':
      return 'Authority violation';
    case 'concurrent_update':
      return 'Concurrent update';
    default:
      return kind;
  }
}

function conflictStatusLabel(status: string): string {
  switch (status) {
    case 'pending_remote_reconciliation':
      return 'Remote reconciliation bekliyor';
    case 'conflict_detected':
      return 'Conflict tespit edildi';
    case 'resolved':
      return 'Çözüldü';
    case 'ignored':
      return 'Yoksayıldı';
    default:
      return status;
  }
}

function ConflictCard({
  conflict,
  note,
  isResolving,
  onNoteChange,
  onResolve,
}: {
  conflict: ConnectSyncConflict;
  note: string;
  isResolving: boolean;
  onNoteChange: (value: string) => void;
  onResolve: (action: ConnectConflictResolutionAction) => void;
}): JSX.Element {
  const isClosed = conflict.status === 'resolved' || conflict.status === 'ignored';
  const remoteStatus = String(conflict.remoteSnapshot?.status ?? '-');
  const remoteOffering = String(conflict.remoteSnapshot?.offeringId ?? '-');

  return (
    <div className="rounded-biz-card border border-biz-border bg-biz-surface p-biz-card">
      <div className="flex flex-col gap-biz-content md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-biz-content">
            <span className="rounded-full bg-biz-muted px-biz-content py-1 text-biz-xs font-semibold text-biz-muted-foreground">
              {conflictKindLabel(conflict.conflictKind)}
            </span>
            <span className={cn(
              'rounded-full px-biz-content py-1 text-biz-xs font-semibold',
              conflict.status === 'conflict_detected'
                ? 'bg-red-500/10 text-red-700'
                : isClosed
                  ? 'bg-emerald-500/10 text-emerald-700'
                  : 'bg-amber-500/10 text-amber-700',
            )}
            >
              {conflictStatusLabel(conflict.status)}
            </span>
          </div>
          <p className="mt-biz-content text-biz-sm font-semibold biz-heading">
            Booking {conflict.externalEntityId}
          </p>
          <p className="mt-1 text-biz-xs text-biz-muted-foreground">
            Remote status: {remoteStatus} · Offering: {remoteOffering} · Detected: {conflict.detectedAt}
          </p>
        </div>
        <div className="text-biz-xs text-biz-muted-foreground">
          Authority: {authorityLabel(conflict.syncAuthority)}
        </div>
      </div>

      {!isClosed ? (
        <div className="mt-biz-content space-y-biz-content">
          <textarea
            value={note}
            onChange={(event) => onNoteChange(event.target.value)}
            placeholder="Çözüm notu / operasyon gerekçesi"
            className="min-h-[84px] w-full rounded-biz-card border border-biz-border bg-biz-surface px-biz-card py-biz-content text-biz-sm outline-none focus:border-biz-primary"
          />
          <div className="flex flex-wrap gap-biz-content">
            <button
              type="button"
              disabled={isResolving}
              onClick={() => onResolve('resolved')}
              className="min-h-biz-touch-primary rounded-biz-card bg-biz-primary px-biz-card text-biz-sm font-semibold text-white disabled:opacity-50"
            >
              Çözüldü işaretle
            </button>
            <button
              type="button"
              disabled={isResolving}
              onClick={() => onResolve('ignored')}
              className="min-h-biz-touch-primary rounded-biz-card border border-biz-border px-biz-card text-biz-sm font-semibold text-biz-muted-foreground disabled:opacity-50"
            >
              Yoksay
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function StatusCard({
  icon,
  title,
  value,
  description,
}: {
  icon: JSX.Element;
  title: string;
  value: string;
  description: string;
}): JSX.Element {
  return (
    <Card>
      <CardBody className="flex min-h-[180px] flex-col justify-between gap-biz-content p-biz-card-lg">
        <div className="flex items-center gap-biz-content text-biz-primary">
          {icon}
          <span className="text-biz-sm font-medium text-biz-muted-foreground">{title}</span>
        </div>
        <div>
          <div className="break-words text-biz-xl font-semibold biz-heading">{value}</div>
          <p className="mt-biz-content text-biz-sm text-biz-muted-foreground">{description}</p>
        </div>
      </CardBody>
    </Card>
  );
}
